// framework/client/core/WidgetBase.js
import { ComponentBase } from "./ComponentBase.js";
import Mustache from 'mustache';

export class WidgetBase extends ComponentBase {
    static baseStyles = `
    .widget-container {
      border: 1px solid var(--bs-border-color);
      border-radius: var(--ab-radius-md);
      box-shadow: var(--ab-shadow-sm);
      transition: box-shadow var(--ab-t-slow), transform var(--ab-t-slow);
      width: 100%;
      position: relative;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      background-color: var(--bs-body-bg);
      color: var(--bs-body-color);
    }

    .widget-container-resizable {
      width: 100%;
    }

    .widget-container-auto {
      margin-bottom: 1rem;
      max-width: 400px;
      flex: 1 1 auto;
    }

    @media (hover: hover) {
      .widget-container:hover {
        box-shadow: var(--ab-shadow-md);
        transform: var(--ab-lift-sm);
      }
    }

    .widget-title-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.5rem 0.75rem;
      background-color: rgba(var(--bs-body-color-rgb), 0.03);
      border-bottom: 1px solid var(--bs-border-color);
      cursor: move;
      flex-shrink: 0;
    }
    .widget-title {
      margin: 0;
      font-size: var(--ab-text-md);
      font-weight: var(--ab-weight-medium);
      color: var(--bs-heading-color, var(--bs-body-color));
    }
    .widget-content {
      padding: 0.75rem;
      overflow: auto;
      flex: 1 1 auto;
      min-height: 0;
      background-color: transparent;
      color: var(--bs-body-color);
    }
    /* Resizer handle (bottom right) */
    .widget-resizer {
      position: absolute;
      bottom: 2px;
      right: 2px;
      width: 15px;
      height: 15px;
      cursor: se-resize;
      background: rgba(var(--bs-body-color-rgb), 0.15);
      border-radius: var(--ab-radius-xs);
      z-index: 2;
      opacity: 0;
      transition: opacity var(--ab-t-fast);
      pointer-events: none;
    }

    .widget-container:hover .widget-resizer {
      opacity: 1;
      pointer-events: auto;
    }

    @media (max-width: 768px) {
      .widget-container {
        min-height: min(360px, 85vw) !important; /* Make it look square on mobile screens */
      }
      .widget-container-resizable {
        width: 100% !important;
        max-width: 100% !important;
      }
    }
    `;

    constructor(config) {
        super(config);
        this.title = config.widget?.title || "Widget";
        this.container = null;
        this.contentContainer = document.createElement('div');
        this.contentContainer.classList.add("widget-content");
        this.isLoading = false;
        this.error = null;
        this.dynamicData = {};
        // Base cell dimensions (should match Dashboard grid min size)
        this.baseCellWidth = 10;   // in pixels
        this.baseCellHeight = 10;  // in pixels
        this.applyStyles('widget-base-styles', WidgetBase.baseStyles);
    }

    async initialize() {
        this.log.info(`Initializing Widget component: ${this.constructor.name} (${this.config.instanceId})`);
        // Save the dashboard instance if passed via props.
        this.dashboard = (this.config.dashboard) ? this.config.dashboard : null;
        await super.initialize();
    }

    async initAfterLogin() {
        // Always subscribe to future date range changes. This will also catch the initial one.
        if (this.dashboard) {
            const eventName = `dashboard-${this.dashboard.config.instanceId}-daterange-changed`;
            this.appClient.events.local.on(eventName, () => this.refresh());
        }

        // Check if this widget's data provider depends on the date range.
        const dependsOnDateRange = JSON.stringify(this.config.widget.dataProvider)?.includes('{{startDate}}');

        // If the widget does NOT depend on the date range, load its data immediately.
        if (!dependsOnDateRange) {
            await this.loadData();
        }
        // If it DOES depend on the date range, check if the data is already available.
        else if (this.appClient.getSharedData('dashboard-date-range')) {
            await this.loadData();
        }
        // Otherwise, do nothing. The event listener we just set up will trigger loadData()
        // as soon as the Dashboard component sets the initial date range.
        else {
            this.log.debug(`Widget ${this.config.instanceId} is waiting for the initial date range to be set.`);
        }

        // Subscribe to other custom events if configured
        if (this.config.widget.events) {
            this.subscribeToEvents(this.config.widget.events);
        }

        // Render the initial (potentially loading) state of the content
        await this.renderContent();
    }

    getWidgetContainerClass() {
        if (this.dashboard && this.dashboard.config.resizableWidgets) {
            return "widget-container-resizable";
        } else {

            return "widget-container-auto";
        }

    }
    async render() {
        if (!this.container) {
            this.container = document.createElement("div");

            const rows = this.config.widget.rows || 20;
            const cols = this.config.widget.cols || 20;
            this.container.style.gridColumn = `span ${cols}`;
            this.container.style.gridRow = `span ${rows}`;

            this.container.classList.add("widget-container", this.getWidgetContainerClass());
            this.container.dataset.instanceId = this.config.instanceId;
            const titleBar = document.createElement("div");
            titleBar.classList.add("widget-title-bar", "d-flex", "justify-content-between", "align-items-center");
            titleBar.setAttribute('draggable', true);
            titleBar.addEventListener('dragstart', (e) => {
                const isButton = e.target.closest('button, input, a, select, textarea');
                if (isButton) {
                    e.preventDefault();
                    return;
                }
                if (e.dataTransfer.setDragImage) {
                    const rect = this.container.getBoundingClientRect();
                    e.dataTransfer.setDragImage(this.container, e.clientX - rect.left, e.clientY - rect.top);
                }
                e.dataTransfer.setData('text/plain', this.config.widgetId);
                this.container.classList.add('dragging');
            });
            titleBar.addEventListener('dragend', () => {
                this.container.classList.remove('dragging');
            });

            const titleElement = document.createElement("div");
            titleElement.classList.add("widget-title");
            titleElement.setAttribute("data-i18n", this.title);
            titleElement.textContent = this.title;
            titleBar.appendChild(titleElement);

            // --- MODIFIED SECTION ---
            if (!(this.dashboard && this.dashboard.config.disableCustomizations)) {
                const closeButton = document.createElement("button");
                closeButton.classList.add("ab-icon-btn", "ab-icon-btn-sm", "text-danger");
                closeButton.setAttribute("aria-label", this.t("Close Widget"));
                closeButton.innerHTML = `<i class="bi bi-x-lg"></i>`;
                closeButton.addEventListener('click', () => this.close());
                titleBar.appendChild(closeButton);
            }
            // --- END MODIFICATION ---

            this.container.appendChild(titleBar);
            this.container.appendChild(this.contentContainer);

            if (this.dashboard && this.dashboard.config.resizableWidgets) {
                const resizer = document.createElement("div");
                resizer.className = "widget-resizer";
                this.container.appendChild(resizer);
                this.addResizeListeners(resizer);
            }

        }
        return this.container;
    }


    // Add mousedown, mousemove, mouseup listeners for interactive resize.
    addResizeListeners(resizer) {
        let startX, startY, startWidth, startHeight;
        let cellWidth = 10;
        let cellHeight = 10;
        let gap = 10;

        const mouseMoveHandler = (e) => {
            const diffX = e.clientX - startX;
            const diffY = e.clientY - startY;
            const newWidth = Math.max(cellWidth, startWidth + diffX);
            const newHeight = Math.max(cellHeight, startHeight + diffY);

            // Correctly account for grid columns and gaps
            const newCols = Math.max(1, Math.round((newWidth + gap) / (cellWidth + gap)));
            const newRows = Math.max(1, Math.round((newHeight + gap) / (cellHeight + gap)));

            this.config.widget.cols = newCols;
            this.config.widget.rows = newRows;
            this.container.style.gridColumn = `span ${newCols}`;
            this.container.style.gridRow = `span ${newRows}`;
        };

        const mouseUpHandler = async (e) => {
            document.removeEventListener('mousemove', mouseMoveHandler);
            document.removeEventListener('mouseup', mouseUpHandler);
            if (this.dashboard) {
                this.dashboard.syncWidgetDimensions(this.config.widgetId, this.config.widget.cols, this.config.widget.rows);
                await this.dashboard.saveDashboardConfig();
            }
        };

        resizer.addEventListener('mousedown', (e) => {
            e.preventDefault();
            startX = e.clientX;
            startY = e.clientY;
            const rect = this.container.getBoundingClientRect();
            startWidth = rect.width;
            startHeight = rect.height;

            if (this.container.parentNode) {
                const parentStyle = window.getComputedStyle(this.container.parentNode);
                const columnsStr = parentStyle.gridTemplateColumns;
                if (columnsStr) {
                    const columnWidths = columnsStr.split(' ').map(parseFloat);
                    if (columnWidths.length > 0 && !isNaN(columnWidths[0])) {
                        cellWidth = columnWidths[0];
                    }
                }
                const gapStr = parentStyle.rowGap || parentStyle.gridRowGap;
                if (gapStr) {
                    const parsedGap = parseFloat(gapStr);
                    if (!isNaN(parsedGap)) {
                        gap = parsedGap;
                    }
                }
                const rowHeightStr = parentStyle.gridAutoRows;
                if (rowHeightStr) {
                    const parsedRowHeight = parseFloat(rowHeightStr);
                    if (!isNaN(parsedRowHeight)) {
                        cellHeight = parsedRowHeight;
                    }
                }
            }

            document.addEventListener('mousemove', mouseMoveHandler);
            document.addEventListener('mouseup', mouseUpHandler);
        });
    }

    async renderContent() {
        throw new Error("renderContent() must be implemented by subclasses of WidgetBase");
    }

    close() {
        if (this.container && this.container.parentNode) {
            this.container.parentNode.removeChild(this.container);
        }
        this.emit('close', { instanceId: this.config.instanceId });
    }

    setLoading(loading) {
        this.isLoading = loading;
        if (this.isLoading) {
            this.contentContainer.innerHTML = `
                <div class="ab-loading">
                    <div class="spinner-border spinner-border-sm text-primary" role="status">
                        <span class="visually-hidden">${this.t('Loading...')}</span>
                    </div>
                    <span>${this.t('Loading...')}</span>
                </div>`;
        } else {
            this.contentContainer.innerHTML = '';
        }
    }

    getData(key) {
        return this.appClient.getSharedData(key);
    }

    setError(error) {
        this.error = error;
        this.contentContainer.innerHTML = `<div class="widget-error text-center py-2 text-danger">Error: ${error.message}</div>`;
    }

    // Helper to retrieve config values.
    getConfig(key, defaultValue) {
        return this.config.widget?.[key] !== undefined ? this.config.widget[key] : defaultValue;
    }

    async callRpc(method, params = {}, context = {}) {
        // Substitute template variables first
        const substitutedParams = this.substituteVariables(params);
        const substitutedContext = this.substituteVariables(context);

        const cacheKey = `${method}::${JSON.stringify(substitutedParams)}::${JSON.stringify(substitutedContext)}`;
        const now = Date.now();
        const TTL_MS = 30_000; // 30-second result cache

        // 1. Return cached result if still fresh
        const cached = WidgetBase._rpcResultCache.get(cacheKey);
        if (cached && (now - cached.ts) < TTL_MS) {
            return cached.data;
        }

        // 2. Deduplicate in-flight requests: return the same Promise to all callers
        if (WidgetBase._rpcInFlight.has(cacheKey)) {
            return WidgetBase._rpcInFlight.get(cacheKey);
        }

        // 3. Make the actual RPC call and cache result on resolution
        const promise = this.appClient.rpcClient.call(method, substitutedParams, substitutedContext)
            .then(data => {
                WidgetBase._rpcResultCache.set(cacheKey, { data, ts: Date.now() });
                WidgetBase._rpcInFlight.delete(cacheKey);
                return data;
            })
            .catch(err => {
                WidgetBase._rpcInFlight.delete(cacheKey);
                throw err;
            });

        WidgetBase._rpcInFlight.set(cacheKey, promise);
        return promise;
    }

    substituteVariables(obj) {
        const dateRange = this.appClient.getSharedData('dashboard-date-range') || {};

        // --- START OF THE FIX ---
        // Ensure startDate and endDate are only created from valid date strings.
        // This prevents `new Date(null)` from creating an epoch timestamp.
        const startDate = dateRange.startDate ? new Date(dateRange.startDate) : null;
        const endDate = dateRange.endDate ? new Date(dateRange.endDate) : null;

        const data = {
            userId: this.appClient.getSharedData('userId'),
            ...this.appClient.getSharedData('context'),
            startDate: (startDate && !isNaN(startDate)) ? startDate.toISOString() : undefined,
            endDate: (endDate && !isNaN(endDate)) ? endDate.toISOString() : undefined,
            ...this.dynamicData,
        };

        const substitute = (value) => {
            if (typeof value === 'string') {
                return Mustache.render(value, data);
            } else if (Array.isArray(value)) {
                return value.map(substitute);
            } else if (typeof value === 'object' && value !== null) {
                return Object.fromEntries(
                    Object.entries(value).map(([k, v]) => [k, substitute(v)])
                );
            }
            return value;
        };
        return substitute(obj);
    }

    processEventData(eventData, transformConfig) {
        if (!transformConfig) { return eventData; }
        let transformedData = { ...eventData };
        
        if (transformConfig.renameFields) {
            for (const [oldName, newName] of Object.entries(transformConfig.renameFields)) {
                if (transformedData[oldName] !== undefined) {
                    transformedData[newName] = transformedData[oldName];
                    delete transformedData[oldName];
                }
            }
        }
        
        if (transformConfig.addFields) {
            for (const [fieldName, expression] of Object.entries(transformConfig.addFields)) {
                try {
                    // --- FIX: Use new Function instead of eval ---
                    // This creates a function: (eventData) => { return <expression>; }
                    const dynamicFn = new Function('eventData', `return ${expression};`);
                    transformedData[fieldName] = dynamicFn(eventData);
                } catch (error) {
                    this.log.warn(`Error evaluating expression for field ${fieldName}:`, error);
                    transformedData[fieldName] = null;
                }
            }
        }
        
        if (transformConfig.removeFields) {
            transformConfig.removeFields.forEach(field => delete transformedData[field]);
        }
        return transformedData;
    }


    subscribeToEvents(events) {
        if (Array.isArray(events)) {
            events.forEach(eventName => {
                this.appClient.events.server.on(eventName, async (eventData) => {
                    const transformedData = this.processEventData(eventData, this.config.widget.transformEventData);
                    await this.handleEvent(eventName, transformedData);
                });
            });
        }
    }

    async handleEvent(eventName, eventData) {
        throw new Error("handleEvent() must be implemented by subclasses of WidgetBase");
    }

    async loadData() {
        this.setLoading(true);
        this.data = {};
        this.error = null;

        const dataProviderConfig = this.getConfig('dataProvider');
        if (!dataProviderConfig) {
            this.setLoading(false);
            return;
        }

        try {
            const fetchPromises = [];
            const sourceKeys = [];

            for (const [key, sourceConfig] of Object.entries(dataProviderConfig)) {
                if (sourceConfig.rpc) {
                    sourceKeys.push(key);
                    const promise = this.callRpc(sourceConfig.rpc, sourceConfig.params || {})
                        .then(response => {
                            if (sourceConfig.transform && typeof sourceConfig.transform === 'function') {
                                return sourceConfig.transform(response);
                            }
                            return response;
                        });
                    fetchPromises.push(promise);
                }
            }

            const results = await Promise.all(fetchPromises);

            results.forEach((result, index) => {
                const key = sourceKeys[index];
                this.data[key] = result;
            });

            if (typeof this.config.widget.enrichData === 'function') {
                this.log.debug(`Enriching data for widget ${this.config.instanceId}`);
                // The enrichData function can modify this.data directly or return a new data object.
                const enriched = await this.config.widget.enrichData(this.data, this.appClient);
                if (enriched) {
                    this.data = enriched;
                }
            }

        } catch (error) {
            this.error = error;
            this.log.error(`Error loading data for widget ${this.config.instanceId}:`, error);
        } finally {
            this.setLoading(false);
        }
    }


    async refresh() {
        this.log.info(`Refreshing widget ${this.config.instanceId}`);
        await this.loadData();
        await this.renderContent();
    }


    destroy() {
        this.appClient.events.local.off("dashboard-date-changed", this.handleDateRangeChange);
        super.destroy();
    }
}

// Shared across all widget instances — keyed by "method::paramsJSON"
WidgetBase._rpcInFlight   = new Map(); // in-flight Promise deduplication
WidgetBase._rpcResultCache = new Map(); // 30-second result cache
